import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect } from "react";
import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";
import { DollarSign, Plus, Edit3, Trash2, X } from "lucide-react";
import {
  getAllDailyAllowances,
  createDailyAllowance,
  updateDailyAllowance,
  deleteDailyAllowance,
} from "../../api/master";
import { getAllDesignations } from "../../api/master";
import { useAuth } from "../../context/AuthContext";

interface Designation {
  id: number;
  name: string;
  rank: number;
}

interface DailyAllowance {
  id: number;
  tenant_id: number;
  designation_id: number;
  head_quarter_amount: number;
  ex_amount: number;
  out_amount: number;
  km: number;
  ta_type: number; // 1 = one-way, 2 = two-way
  designation?: Designation;
  updated_at?: string;
}

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/** Pill-style toggle: OFF = One Way, ON = Two Way */
const TaTypeToggle = ({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (newVal: number) => void;
  disabled?: boolean;
}) => {
  const isTwoWay = value === 2;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(isTwoWay ? 1 : 2)}
      className={`relative inline-flex h-6 w-12 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
        isTwoWay ? "bg-blue-600" : "bg-gray-300"
      }`}
      title={isTwoWay ? "Two Way (click to switch to One Way)" : "One Way (click to switch to Two Way)"}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
          isTwoWay ? "translate-x-6" : "translate-x-0"
        }`}
      />
    </button>
  );
};

const DailyAllowanceMaster: React.FC = () => {
  const { user } = useAuth();
  const [allowances, setAllowances] = useState<DailyAllowance[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentId, setCurrentId] = useState(0);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    if (user?.tenant_id) fetchAll();
  }, [user]);

  const fetchAll = async () => {
    try {
      const [allowanceData, designationData] = await Promise.all([
        getAllDailyAllowances(user.tenant_id),
        getAllDesignations(user.tenant_id),
      ]);
      setAllowances(allowanceData ?? []);
      setDesignations(designationData ?? []);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const takenDesignationIds = new Set(allowances.map((a) => a.designation_id));
  const availableDesignations = designations.filter(
    (d) => !takenDesignationIds.has(d.id)
  );

  const schema = Yup.object({
    designation_id: Yup.number()
      .min(1, "Please select a designation")
      .required("Designation is required"),
    head_quarter_amount: Yup.number().min(0).required("HQ amount is required"),
    ex_amount: Yup.number().min(0).required("EX amount is required"),
    out_amount: Yup.number().min(0).required("OUT amount is required"),
    km: Yup.number().min(0).required("KM is required"),
    ta_type: Yup.number().oneOf([1, 2]).required(),
  });

  const {
    setValues,
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    handleSubmit,
    errors,
    touched,
    resetForm,
  } = useFormik({
    initialValues: {
      designation_id: 0,
      head_quarter_amount: 0,
      ex_amount: 0,
      out_amount: 0,
      km: 0,
      ta_type: 1,
    },
    validationSchema: schema,
    onSubmit: async (vals) => {
      try {
        if (!isEditModalOpen) {
          const response = await createDailyAllowance(
            user.tenant_id,
            vals.designation_id,
            vals.head_quarter_amount,
            vals.ex_amount,
            vals.out_amount,
            vals.km,
            vals.ta_type
          );
          if (response.status === 200) {
            setIsAddModalOpen(false);
            resetForm();
            await fetchAll();
            toast.success("Daily Allowance Created Successfully!");
          }
        } else {
          const response = await updateDailyAllowance(
            currentId,
            vals.head_quarter_amount,
            vals.ex_amount,
            vals.out_amount,
            vals.km,
            vals.ta_type
          );
          if (response.status === 200) {
            setIsEditModalOpen(false);
            resetForm();
            await fetchAll();
            toast.success("Daily Allowance Updated Successfully!");
          }
        }
      } catch (error: any) {
        const msg = error?.response?.data?.message || error.message || "Something went wrong.";
        toast.error(msg);
      }
    },
  });

  const handleEdit = (item: DailyAllowance) => {
    setValues({
      designation_id: item.designation_id,
      head_quarter_amount: item.head_quarter_amount,
      ex_amount: item.ex_amount,
      out_amount: item.out_amount,
      km: item.km ?? 0,
      ta_type: item.ta_type ?? 1,
    });
    setCurrentId(item.id);
    setIsEditModalOpen(true);
  };

  /** Inline toggle — flips ta_type without opening the modal */
  const handleToggleTaType = async (item: DailyAllowance) => {
    const newType = item.ta_type === 2 ? 1 : 2;
    setTogglingId(item.id);
    try {
      const response = await updateDailyAllowance(
        item.id,
        item.head_quarter_amount,
        item.ex_amount,
        item.out_amount,
        item.km ?? 0,
        newType
      );
      if (response.status === 200) {
        setAllowances((prev) =>
          prev.map((a) => (a.id === item.id ? { ...a, ta_type: newType } : a))
        );
      }
    } catch (error) {
      toast.error("Failed to update TA type.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!await toast.confirm("This allowance record will be deleted.", "Delete allowance?", "Delete")) return;
    try {
      await deleteDailyAllowance(id);
      setAllowances((prev) => prev.filter((a) => a.id !== id));
      toast.success("Allowance record removed.");
    } catch (error) {
      toast.error("Failed to delete allowance.");
    }
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    resetForm();
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isEditModalOpen && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
          <select
            name="designation_id"
            value={values.designation_id}
            onBlur={handleBlur}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value={0}>-- Select Designation --</option>
            {availableDesignations.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          {errors.designation_id && touched.designation_id && (
            <p className="text-red-500 text-sm mt-1">{errors.designation_id}</p>
          )}
          {availableDesignations.length === 0 && (
            <p className="text-yellow-600 text-sm mt-1">All designations already have allowances configured.</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">HQ Amount (₹) *</label>
          <input type="number" name="head_quarter_amount" value={values.head_quarter_amount}
            onBlur={handleBlur} onChange={handleChange} min={0} step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.head_quarter_amount && touched.head_quarter_amount && (
            <p className="text-red-500 text-sm mt-1">{errors.head_quarter_amount}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">EX Amount (₹) *</label>
          <input type="number" name="ex_amount" value={values.ex_amount}
            onBlur={handleBlur} onChange={handleChange} min={0} step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.ex_amount && touched.ex_amount && (
            <p className="text-red-500 text-sm mt-1">{errors.ex_amount}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">OUT Amount (₹) *</label>
          <input type="number" name="out_amount" value={values.out_amount}
            onBlur={handleBlur} onChange={handleChange} min={0} step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.out_amount && touched.out_amount && (
            <p className="text-red-500 text-sm mt-1">{errors.out_amount}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">KM Rate *</label>
          <input type="number" name="km" value={values.km}
            onBlur={handleBlur} onChange={handleChange} min={0} step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.km && touched.km && (
            <p className="text-red-500 text-sm mt-1">{errors.km}</p>
          )}
        </div>
      </div>

      {/* TA Type toggle inside the form */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Travel Allowance Type</label>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${values.ta_type === 1 ? "text-gray-900" : "text-gray-400"}`}>
            One Way
          </span>
          <TaTypeToggle
            value={values.ta_type}
            onChange={(v) => setFieldValue("ta_type", v)}
          />
          <span className={`text-sm font-medium ${values.ta_type === 2 ? "text-blue-600" : "text-gray-400"}`}>
            Two Way
          </span>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          {values.ta_type === 2
            ? "TA will be calculated for both outward and return journey (×2)."
            : "TA will be calculated for outward journey only (×1)."}
        </p>
      </div>

      <div className="flex justify-end space-x-3 pt-2">
        <button type="button" onClick={closeModal}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          {isEditModalOpen ? "Update" : "Add"}
        </button>
      </div>
    </form>
  );

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Daily Allowance</h1>
            <p className="text-blue-600 mt-1">Configure daily allowances by designation</p>
          </div>
          <button
            onClick={() => { resetForm(); setIsAddModalOpen(true); }}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Allowance
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Allowance Records ({allowances.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">SN</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Designation</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Rank</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">HQ Amount (₹)</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">EX Amount (₹)</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">OUT Amount (₹)</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">KM Rate</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">TA Type</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Updated At</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allowances.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-gray-500">
                        No allowance records found.
                      </td>
                    </tr>
                  ) : allowances.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-4 font-medium text-gray-900">{index + 1}</td>
                      <td className="py-4 px-4 text-gray-900 font-medium">
                        {item.designation?.name ?? "-"}
                      </td>
                      <td className="py-4 px-4">
                        {item.designation?.rank != null ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 font-medium">
                            Rank {item.designation.rank}
                          </span>
                        ) : "-"}
                      </td>
                      <td className="py-4 px-4 text-gray-900">
                        {Number(item.head_quarter_amount).toFixed(2)}
                      </td>
                      <td className="py-4 px-4 text-gray-900">
                        {Number(item.ex_amount).toFixed(2)}
                      </td>
                      <td className="py-4 px-4 text-gray-900">
                        {Number(item.out_amount).toFixed(2)}
                      </td>
                      <td className="py-4 px-4 text-gray-900">
                        {Number(item.km ?? 0).toFixed(2)}
                      </td>

                      {/* ── TA Type inline toggle ───────────────────── */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <TaTypeToggle
                            value={item.ta_type ?? 1}
                            onChange={() => handleToggleTaType(item)}
                            disabled={togglingId === item.id}
                          />
                          <span className={`text-xs font-semibold whitespace-nowrap ${
                            (item.ta_type ?? 1) === 2 ? "text-blue-600" : "text-gray-500"
                          }`}>
                            {(item.ta_type ?? 1) === 2 ? "Two Way" : "One Way"}
                          </span>
                        </div>
                      </td>

                      <td className="py-4 px-4 text-gray-500 text-sm">
                        {item.updated_at ? fmtDate(item.updated_at) : "-"}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Modal isOpen={isAddModalOpen} onClose={closeModal} title="Add Daily Allowance">
          {renderForm()}
        </Modal>

        <Modal isOpen={isEditModalOpen} onClose={closeModal} title="Edit Daily Allowance">
          {renderForm()}
        </Modal>
      </div>
    </div>
  );
};

export default DailyAllowanceMaster;
